import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { notifications } from "@mantine/notifications";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as yup from "yup";
import { CreationForm } from "../../../components/form/CreationForm";
import { PageHeader } from "../../../components/PageHeader";
import { FormSkeleton } from "../../../components/SkeletonLoader";
import {
  CUSTOM_FIELD_DEFINITION_FRAGMENT,
  CUSTOM_FIELD_TYPE_OPTIONS,
  CustomFieldDefinition,
  CustomFieldType,
  EDITABILITY_OPTIONS,
  VISIBILITY_OPTIONS,
} from "../shared/customFieldFragments";
import { OptionsEditor } from "../shared/OptionsEditor";
import { useAllowedObjectTypes } from "../shared/objectTypeOptions";

const DEFINITIONS_QUERY = gql`
  query customFieldDefinitions($objectType: ObjectType!) {
    customFieldDefinitions(objectType: $objectType) {
      ...CustomFieldDefinitionFields
    }
  }
  ${CUSTOM_FIELD_DEFINITION_FRAGMENT}
`;

const UPDATE_DEFINITION = gql`
  mutation updateCustomFieldDefinition(
    $id: ID!
    $input: UpdateCustomFieldDefinitionInput!
  ) {
    updateCustomFieldDefinition(id: $id, input: $input) {
      ...CustomFieldDefinitionFields
    }
  }
  ${CUSTOM_FIELD_DEFINITION_FRAGMENT}
`;

export const CustomFieldDefinitionEdit = () => {
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const definitionId = useParams().id!;
  const [searchParams] = useSearchParams();
  const objectType = searchParams.get("objectType") ?? "";

  const { data, loading } = useQuery<{
    customFieldDefinitions: CustomFieldDefinition[];
  }>(DEFINITIONS_QUERY, {
    variables: { objectType },
    skip: !objectType,
  });

  const definition = data?.customFieldDefinitions?.find(
    (d) => d.id === definitionId,
  );

  const [updateMutation] = useMutation(UPDATE_DEFINITION);
  const { options: objectTypeOptions } = useAllowedObjectTypes();

  const showOptions =
    definition?.type === CustomFieldType.SELECT ||
    definition?.type === CustomFieldType.MULTI_SELECT;

  if (loading) {
    return (
      <div className="wp-fade-in">
        <PageHeader
          title="Edit Custom Field Definition"
          showBackButton
          showBreadcrumbs={false}
        />
        <FormSkeleton />
      </div>
    );
  }

  if (!objectType) {
    return (
      <div className="wp-fade-in">
        <PageHeader
          title="Edit Custom Field Definition"
          showBackButton
          showBreadcrumbs={false}
        />
        <p>
          Missing objectType parameter. Please navigate from the definitions
          list.
        </p>
      </div>
    );
  }

  if (!definition) {
    return (
      <div className="wp-fade-in">
        <PageHeader
          title="Edit Custom Field Definition"
          showBackButton
          showBreadcrumbs={false}
        />
        <p>Custom field definition not found.</p>
      </div>
    );
  }

  return (
    <div className="wp-fade-in">
      <PageHeader
        title="Edit Custom Field Definition"
        showBackButton
        showBreadcrumbs={false}
      />
      <CreationForm
        title={`Edit "${definition.name}"`}
        schemaDefinition={{
          objectType: {
            type: "SELECT",
            label: "Object Type",
            yupConfig: yup.string().required(),
            options: objectTypeOptions,
            defaultValue: definition.objectType,
            disabled: true,
          },
          key: {
            type: "INPUT",
            label: "Key",
            yupConfig: yup.string().required(),
            inputType: "text",
            defaultValue: definition.key,
            disabled: true,
            subtext: "Key cannot be changed after creation",
          },
          name: {
            type: "INPUT",
            label: "Name",
            yupConfig: yup.string().required("Name is required").max(200),
            inputType: "text",
            defaultValue: definition.name,
          },
          description: {
            type: "TEXTAREA",
            label: "Description",
            yupConfig: yup.string().max(1024).nullable(),
            defaultValue: definition.description ?? "",
          },
          type: {
            type: "SELECT",
            label: "Field Type",
            yupConfig: yup.string().required(),
            options: CUSTOM_FIELD_TYPE_OPTIONS,
            defaultValue: definition.type,
            disabled: true,
            subtext: "Type cannot be changed after creation",
          },
          required: {
            type: "CHECKBOX",
            label: "Required",
            yupConfig: yup.boolean(),
            defaultValue: definition.required,
          },
          unique: {
            type: "CHECKBOX",
            label: "Unique",
            yupConfig: yup.boolean(),
            defaultValue: definition.unique,
          },
          visibility: {
            type: "SELECT",
            label: "Visibility",
            yupConfig: yup.string().required("Visibility is required"),
            options: VISIBILITY_OPTIONS,
            defaultValue: definition.visibility,
          },
          editability: {
            type: "SELECT",
            label: "Editability",
            yupConfig: yup.string().required("Editability is required"),
            options: EDITABILITY_OPTIONS,
            defaultValue: definition.editability,
          },
          // showOptions is static because type is immutable after creation (disabled: true)
          ...(showOptions && {
            options: {
              type: "DYNAMIC" as const,
              label: "Options",
              yupConfig: yup
                .array()
                .max(200, "Maximum 200 options allowed")
                .of(
                  yup.object({
                    label: yup.string().required().max(200, "Label too long"),
                    value: yup.string().required().max(200, "Value too long"),
                  }),
                ),
              defaultValue: definition.options ?? [{ label: "", value: "" }],
              element: (onChange: (...args: any[]) => void) => (
                <OptionsEditor onChange={onChange} value={definition.options} />
              ),
            },
          }),
          defaultValue: {
            type: "INPUT",
            label: "Default Value",
            yupConfig: yup.string().max(4096).nullable(),
            inputType: "text",
            defaultValue: definition.defaultValue ?? "",
          },
          validationRegex: {
            type: "INPUT",
            label: "Validation Regex",
            yupConfig: yup
              .string()
              .max(500)
              .nullable()
              .test(
                "valid-regex",
                "Must be a valid regular expression without nested quantifiers",
                (value) => {
                  if (!value) return true;
                  try {
                    new RegExp(value);
                    if (/(\(.*[+*].*\)[+*]|\.\*.*\.\*)/.test(value)) {
                      return false;
                    }
                    return true;
                  } catch {
                    return false;
                  }
                },
              ),
            inputType: "text",
            defaultValue: definition.validationRegex ?? "",
          },
        }}
        onSubmit={async (values) => {
          const input = {
            name: values.name,
            description: values.description || undefined,
            required: values.required ?? false,
            unique: values.unique ?? false,
            visibility: values.visibility,
            editability: values.editability,
            options: values.options?.length > 0 ? values.options : undefined,
            defaultValue: values.defaultValue || undefined,
            validationRegex: values.validationRegex || undefined,
          };

          try {
            await updateMutation({
              variables: { id: definitionId, input },
            }).then((result) => {
              if (result?.error) {
                throw new Error(result.error.message);
              }
            });

            notifications.show({
              title: "Field updated",
              message: `Custom field "${values.name}" has been updated`,
              color: "green",
              autoClose: 3000,
            });

            navigate("/data/custom-fields");
          } catch (error) {
            notifications.show({
              title: "Update failed",
              message:
                error instanceof Error && error.message.length < 200
                  ? error.message
                  : "Failed to update custom field",
              color: "red",
              autoClose: 5000,
            });
          }
        }}
      />
    </div>
  );
};
